import React, { useState, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  Shield,
  CheckCircle2,
  Wallet,
  Receipt,
  Sparkles,
  Glasses,
  Eye,
  Info,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { itemDisplayName, OWN_FRAME_NAME } from '../../utils/itemDisplayName';
import { isTwoPairSale, pairStepData } from '../../utils/salePairs';

// ────────────────────────────────────────────────────────────
// Tokens
// ────────────────────────────────────────────────────────────
const t = {
  bg: '#f6f7fb',
  surface: '#ffffff',
  surfaceMuted: '#fafbff',
  ink: '#0f172a',
  inkSoft: '#475569',
  inkMuted: '#94a3b8',
  hairline: '#e5e7eb',
  hairlineSoft: '#eef0f5',
  brand: '#6366f1',
  brandStrong: '#4f46e5',
  brandSoft: '#eef2ff',
  ok: '#10b981',
  okSoft: '#ecfdf5',
  warn: '#f59e0b',
  warnSoft: '#fffbeb',
  danger: '#ef4444',
  dangerSoft: '#fef2f2',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Layout ──────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: ${t.bg};
  background-image:
    radial-gradient(900px 480px at 0% -10%, rgba(99, 102, 241, 0.08), transparent 60%),
    radial-gradient(700px 420px at 110% -10%, rgba(168, 85, 247, 0.07), transparent 60%);
  padding: 0.85rem 1rem 1rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, sans-serif;
  color: ${t.ink};

  @media (max-width: 640px) {
    padding: 0.5rem;
  }
`;

const Shell = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.7rem;
  background: ${t.surface};
  border: 1px solid ${t.hairline};
  border-radius: 999px;
  color: ${t.inkSoft};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${t.brand};
    border-color: ${t.brand}40;
    background: ${t.brandSoft};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const Title = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${t.ink};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${t.inkSoft};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 1rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// ── Cards ───────────────────────────────────────────────────
const Card = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.hairline};
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  padding: 1rem 1.1rem;
  animation: ${fadeUp} 0.3s ease both;
`;

// ── Order summary (right panel) ─────────────────────────────
const SummaryCard = styled(Card)`
  position: sticky;
  top: 0.85rem;
  background: linear-gradient(180deg, #1e1b4b 0%, #312e81 60%, #3730a3 100%);
  border: 1px solid #4338ca;
  color: #fff;
  overflow: hidden;
  isolation: isolate;
  padding: 0.95rem 1rem;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(500px 240px at 90% 0%, rgba(168, 85, 247, 0.4), transparent 60%),
      radial-gradient(500px 280px at -10% 110%, rgba(99, 102, 241, 0.45), transparent 60%);
    z-index: -1;
    pointer-events: none;
  }

  @media (max-width: 900px) {
    position: static;
  }
`;

const SummaryEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.65rem;
`;

const SummaryItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-bottom: 0.7rem;
`;

// "Pair 1 · far vision" / "Pair 2 · close up" over each pair's lines.
const SummaryPairHeading = styled.div`
  margin: 0.6rem 0 0.2rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
`;

const SummaryItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.55rem;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  backdrop-filter: blur(8px);
`;

const SummaryItemIcon = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
`;

const SummaryItemMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const SummaryItemName = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SummaryItemMeta = styled.div`
  font-size: 0.66rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SummaryItemPrice = styled.div`
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: #fff;
  font-variant-numeric: tabular-nums;
`;

const SummaryDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  margin: 0.6rem 0;
`;

const SummaryTotalRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const SummaryTotalLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.7);
`;

const SummaryTotalValue = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #fff;
  font-variant-numeric: tabular-nums;
`;

const SummaryTotalCurrency = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  margin-left: 0.25rem;
`;

// ── Section header inside main card ────────────────────────
const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.7rem;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${t.ink};

  svg {
    width: 15px;
    height: 15px;
    color: ${t.brand};
  }
`;

// ── Payment progress (compact) ─────────────────────────────
const ProgressBlock = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.7rem;
  align-items: center;
  padding: 0.7rem 0.85rem;
  background: ${t.surfaceMuted};
  border: 1px solid ${t.hairlineSoft};
  border-radius: 12px;
  margin-bottom: 0.75rem;
`;

const ProgressPercent = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 0.15rem;
  font-size: 1.45rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: ${(p) => (p.$met ? t.ok : p.$empty ? t.inkMuted : t.warn)};
  transition: color 0.2s ease;
  font-variant-numeric: tabular-nums;

  span.suffix {
    font-size: 0.8rem;
    font-weight: 600;
    color: ${t.inkMuted};
  }
`;

const ProgressBarStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ProgressTrack = styled.div`
  position: relative;
  height: 8px;
  background: ${t.hairlineSoft};
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${(p) =>
    p.$met
      ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
      : 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)'};
  width: ${(p) => Math.min(p.$percentage, 100)}%;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1), background 0.2s ease;
`;

const ProgressMarker = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  background: ${t.surface};
  box-shadow: 0 0 0 1px ${t.inkMuted}55;
`;

const ProgressLine = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.68rem;
  color: ${t.inkSoft};
  font-variant-numeric: tabular-nums;

  strong {
    color: ${t.ink};
    font-weight: 600;
  }
`;

// ── Status pill ─────────────────────────────────────────────
const StatusPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.7rem;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
  line-height: 1.35;

  ${(p) =>
    p.tone === 'ok' &&
    css`
      background: ${t.okSoft};
      color: #047857;
      border: 1px solid #6ee7b7;
    `}
  ${(p) =>
    p.tone === 'warn' &&
    css`
      background: ${t.warnSoft};
      color: #b45309;
      border: 1px solid #fcd34d;
    `}
  ${(p) =>
    p.tone === 'info' &&
    css`
      background: ${t.brandSoft};
      color: ${t.brandStrong};
      border: 1px solid #c7d2fe;
    `}

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  strong {
    font-weight: 700;
  }
`;

// ── Amount input + chips on the same row ───────────────────
const AmountLabel = styled.label`
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  color: ${t.inkSoft};
  margin-bottom: 0.35rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) auto;
  gap: 0.5rem;
  align-items: stretch;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AmountInputBox = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  background: ${t.surfaceMuted};
  border: 1.5px solid ${(p) => (p.$focused ? t.brand : t.hairline)};
  border-radius: 12px;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$focused ? `0 0 0 4px ${t.brand}15` : 'none')};
  height: 56px;

  &:hover {
    border-color: ${(p) => (p.$focused ? t.brand : t.inkMuted)};
  }
`;

const AmountInput = styled.input`
  flex: 1;
  width: 100%;
  padding: 0 0.95rem;
  border: none;
  background: transparent;
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${t.ink};
  outline: none;
  font-variant-numeric: tabular-nums;

  &::placeholder {
    color: ${t.inkMuted};
    font-weight: 500;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const AmountSuffix = styled.div`
  display: flex;
  align-items: center;
  padding: 0 0.85rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${t.inkMuted};
  background: ${t.surface};
  border-left: 1px solid ${t.hairline};
  border-radius: 0 11px 11px 0;
  letter-spacing: 0.05em;
`;

const QuickChips = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(80px, 1fr));
  gap: 0.4rem;
  height: 56px;

  @media (max-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Chip = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  padding: 0 0.4rem;
  border-radius: 11px;
  border: 1.5px solid ${(p) => (p.$active ? t.brand : t.hairline)};
  background: ${(p) => (p.$active ? t.brandSoft : t.surface)};
  color: ${(p) => (p.$active ? t.brandStrong : t.inkSoft)};
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover:not(:disabled) {
    border-color: ${t.brand};
    color: ${t.brandStrong};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .label {
    font-size: 0.74rem;
    font-weight: 700;
    line-height: 1.1;
  }
  .amount {
    font-size: 0.62rem;
    font-weight: 500;
    color: ${(p) => (p.$active ? t.brandStrong : t.inkMuted)};
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }
`;

// ── Balance + Waiver row ───────────────────────────────────
const BalanceWaiverRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$showWaiver ? '1fr 1.2fr' : '1fr')};
  gap: 0.55rem;
  margin-top: 0.65rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const BalancePill = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.8rem;
  background: ${t.surfaceMuted};
  border: 1px solid ${t.hairlineSoft};
  border-radius: 12px;

  .left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: ${t.brandSoft};
    color: ${t.brandStrong};
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .label {
    font-size: 0.65rem;
    font-weight: 700;
    color: ${t.inkSoft};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    line-height: 1.1;
  }
  .hint {
    font-size: 0.65rem;
    color: ${t.inkMuted};
    line-height: 1.1;
  }

  .value {
    font-size: 1rem;
    font-weight: 700;
    color: ${t.ink};
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
  }
`;

const WaiverCard = styled.label`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.55rem;
  padding: 0.6rem 0.75rem;
  background: ${t.warnSoft};
  border: 1.5px solid ${(p) => (p.$active ? t.warn : '#fcd34d')};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  align-items: center;

  ${(p) =>
    !p.$active &&
    css`
      animation: pulseW 2.4s ease-in-out infinite;
    `}

  @keyframes pulseW {
    0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.25); }
    50%      { box-shadow: 0 0 0 4px rgba(245, 158, 11, 0); }
  }

  &:hover {
    border-color: ${t.warn};
  }
`;

const WaiverCheckbox = styled.input.attrs({ type: 'checkbox' })`
  margin: 0;
  width: 16px;
  height: 16px;
  accent-color: ${t.warn};
  cursor: pointer;
`;

const WaiverText = styled.div`
  font-size: 0.7rem;
  color: #78350f;
  line-height: 1.35;

  strong {
    display: block;
    font-size: 0.74rem;
    color: #92400e;
    margin-bottom: 1px;
    font-weight: 700;
  }
`;

// ── Footer ──────────────────────────────────────────────────
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding-top: 0.8rem;
  border-top: 1px solid ${t.hairlineSoft};

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.55rem 0.9rem;
  background: ${t.surface};
  border: 1px solid ${t.hairline};
  border-radius: 10px;
  color: ${t.inkSoft};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${t.ink};
    border-color: ${t.inkMuted};
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  padding: 0.55rem 0.75rem;
  background: ${t.dangerSoft};
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #b91c1c;
  font-size: 0.78rem;
  margin-top: 0.6rem;
  line-height: 1.35;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

// ────────────────────────────────────────────────────────────
const Payment = () => {
  const { t: tr } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deposit, setDeposit] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [salesPassesOrderUnderResponsibility, setSalesPassesOrderUnderResponsibility] =
    useState(false);

  const [orderSummary, setOrderSummary] = useState({
    subtotal: 0,
    tax: 0,
    discount: 0,
    total: 0,
    items: [],
  });

  // Load saved payment data on mount
  useEffect(() => {
    const savedPaymentDataStr = sessionStorage.getItem('paymentFormData');
    if (savedPaymentDataStr) {
      try {
        const savedData = JSON.parse(savedPaymentDataStr);
        if (savedData.deposit !== undefined && savedData.deposit !== null && savedData.deposit !== '') {
          setDeposit(String(savedData.deposit));
        }
        if (savedData.salesPassesOrderUnderResponsibility === true) {
          setSalesPassesOrderUnderResponsibility(true);
        }
      } catch (err) {
        console.error('Error loading saved payment data:', err);
      }
    }
  }, []);

  // Auto-save payment data
  useEffect(() => {
    sessionStorage.setItem(
      'paymentFormData',
      JSON.stringify({ deposit, salesPassesOrderUnderResponsibility })
    );
  }, [deposit, salesPassesOrderUnderResponsibility]);

  useEffect(() => {
    const calculateOrderSummary = async () => {
      try {
        // A two-pair sale prices pair 1 (far) here and pair 2 (close up) below.
        const twoPairs = isTwoPairSale();
        const farData = pairStepData('far');
        const selectedFrame = farData.selectedFrame || {};
        const prescriptionId = sessionStorage.getItem('prescriptionId');
        const selectedLens = farData.selectedLens;
        const draftOrder = farData.draftOrder;
        const draftLensItem = (draftOrder?.items || draftOrder?.order_items || [])
          .find((item) => (item.type || item.item_type) === 'lens');

        const clientHasOwnFrame = selectedFrame.hasOwnFrame === true;
        const framePrice = clientHasOwnFrame ? 0 : (selectedFrame.price || selectedFrame.framePrice || 0);

        let lensPrice = Number(selectedLens?.price ?? draftLensItem?.unitPrice ?? draftLensItem?.unit_price ?? 0) || 0;
        let lensName = selectedLens?.name || draftLensItem?.name || 'Prescription Lenses';
        let orderType = selectedLens?.orderType || null;
        // Progressive lenses always take 7 days (owner's rule), whatever the
        // lens availability says; single vision keeps the lens's own time.
        let isProgressive = false;
        try {
          isProgressive = JSON.parse(sessionStorage.getItem('prescriptionFormData') || '{}').prescriptionType === 'progressive';
        } catch (_) { /* ignore */ }

        if (prescriptionId) {
          try {
            // Send the auth token so the backend can resolve tenant context
            // and find the prescription in the correct (tenant) database.
            const token = localStorage.getItem('token');
            const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
            const prescriptionResponse = await axios.get(
              `/api/prescriptions/${prescriptionId}`,
              { headers: authHeaders }
            );
            const prescription = prescriptionResponse.data;

            if (prescription) {
              orderType = orderType || prescription.orderType;
              if (prescription.prescriptionType) isProgressive = prescription.prescriptionType === 'progressive';
              if (!selectedLens?.name && prescription.orderType === 'professional' && prescription.selectedRecommendation) {
                lensName = prescription.selectedRecommendation.name || 'Professional Recommendation';
                lensPrice = Number(prescription.selectedRecommendation.price) || lensPrice;
              } else if (!selectedLens?.name && prescription.orderType === 'other' && prescription.selectedRecommendation) {
                lensName = prescription.selectedRecommendation.name || 'Other - Selected Product';
                lensPrice = Number(prescription.selectedRecommendation.price) || lensPrice;
              } else if (prescription.orderType === 'buyer' || prescription.orderType === 'other') {
                lensPrice = lensPrice || 60000;
                lensName = selectedLens?.name || lensName || 'Other - Custom Lenses';
              } else {
                if (prescription.prescriptionType === 'single_vision') {
                  lensPrice = lensPrice || 15000;
                  lensName = selectedLens?.name || lensName || 'Single Vision Prescription Lenses';
                } else if (prescription.prescriptionType === 'progressive') {
                  lensPrice = lensPrice || 45000;
                  lensName = selectedLens?.name || lensName || 'Progressive Prescription Lenses';
                } else {
                  lensPrice = lensPrice || 15000;
                  lensName = selectedLens?.name || lensName || 'Prescription Lenses';
                }
              }
            }
          } catch (prescriptionError) {
            console.error('Error fetching prescription:', prescriptionError);
            lensPrice = 15000;
            lensName = 'Prescription Lenses';
          }
        } else {
          lensPrice = 15000;
          lensName = 'Prescription Lenses';
        }

        // One pair's frame line(s): its frame (or the client's own) plus any
        // spare frames picked with it.
        const frameItemsFor = (frame_, extras_) => {
          const lines = [];
          const own_ = frame_.hasOwnFrame === true;
          const price_ = own_ ? 0 : (frame_.price || frame_.framePrice || 0);
          if (!own_ && price_ > 0) {
            const frameProductId = frame_._id || frame_.id || frame_.frameProductId || null;
            lines.push({
              type: 'frame',
              name: frame_.name || 'Frame',
              brand: frame_.brand || '',
              model: frame_.model || '',
              material: frame_.material || '',
              color: frame_.color || '',
              quantity: 1,
              unitPrice: price_,
              totalPrice: price_,
              // Carry the product id so stock deduction matches the exact frame (not by name)
              frameProductId,
              specifications: { frameProductId },
            });
          } else if (own_) {
            lines.push({
              type: 'frame',
              // Stored canonical, never translated: this string lands in order_items and is
              // read by the lab, central and reports. Rendering goes through frameDisplayName.
              name: OWN_FRAME_NAME,
              brand: '',
              model: '',
              material: '',
              color: '',
              quantity: 1,
              unitPrice: 0,
              totalPrice: 0,
            });
          }

          // Extra frames (spares/backups) picked on the Frame Selection step —
          // frame-only add-ons, no lens attached. Each is its own order_items
          // line with its own frameProductId, so stock deducts correctly per
          // frame (applySaleStockMovement walks every 'frame'-typed line, not
          // just the first).
          let extrasTotal = 0;
          const extraFrames = extras_;
          for (const extraFrame of (Array.isArray(extraFrames) ? extraFrames : [])) {
            const extraFramePrice = Number(extraFrame.price) || 0;
            if (extraFramePrice <= 0) continue;
            extrasTotal += extraFramePrice;
            lines.push({
              type: 'frame',
              name: extraFrame.name || 'Frame',
              brand: extraFrame.brand || '',
              model: extraFrame.model || '',
              material: extraFrame.material || '',
              color: extraFrame.color || '',
              quantity: 1,
              unitPrice: extraFramePrice,
              totalPrice: extraFramePrice,
              frameProductId: extraFrame._id || extraFrame.id || extraFrame.frameProductId || null,
              specifications: { frameProductId: extraFrame._id || extraFrame.id || extraFrame.frameProductId || null },
            });
          }
          return { lines, extrasTotal, total: price_ + extrasTotal };
        };
        const lensItemFor = (lens_, lensName_, lensPrice_, lensDays_, visionPair_ = null) => ({
          type: 'lens',
          // Real/supplier name — kept as the item name because stock matching
          // and the central warehouse key off it. The client-facing commercial
          // name rides along in specifications.
          name: lensName_,
          brand: lens_?.brand || '',
          model: lens_?.indexValue ? `Index ${lens_.indexValue}` : '',
          material: lens_?.catalogGroup || '',
          quantity: 1,
          unitPrice: lensPrice_,
          totalPrice: lensPrice_,
          estimatedDays: lensDays_,
          specifications: {
            ...(visionPair_ ? { visionPair: visionPair_ } : {}),
            commercialName: lens_?.commercialName || '',
            estimatedDays: lensDays_,
            sourceType: lens_?.dispo || lens_?.stockType || 'stock',
            inStock: !!lens_?.inStock,
            // Which slot the salesperson picked, frozen onto the line so
            // analytics never has to re-derive it. Progressive tier reads:
            // recommendation → fair, "Other (Up)" → premium, "(Down)" → modest.
            lensDirection: lens_?.direction || null,
            tier: lens_?.direction === 'up' ? 'premium'
              : lens_?.direction === 'down' ? 'modest'
                : 'fair',
            deliveryLabel: isProgressive
              ? `${lensDays_} days`
              : (lens_?.defaultDeliveryLabel || (lensDays_ > 0 ? `~${lensDays_} days` : null)),
          },
        });

        const farFrames = frameItemsFor(selectedFrame, farData.extraFrames);
        const items = [...farFrames.lines];
        const extraFramesTotal = farFrames.extrasTotal;

        if (lensPrice <= 0) {
          lensPrice = 15000;
          lensName = lensName || 'Prescription Lenses';
        }

        // Pull lens metadata so OrderConfirmation can show real delivery + index info
        const PROGRESSIVE_LEAD_DAYS = 7;
        const lensEstimatedDays = isProgressive ? PROGRESSIVE_LEAD_DAYS : (Number(
          selectedLens?.estimatedDays
          ?? selectedLens?.defaultDeliveryDaysMax
          ?? draftLensItem?.specifications?.estimatedDays
          ?? 0
        ) || 0);
        items.push(lensItemFor(selectedLens, lensName, lensPrice, lensEstimatedDays, twoPairs ? 'far' : null));

        // Pair 2 (close up): its own frame, extras and lens, one shared total.
        let pairs = null;
        if (twoPairs) {
          const nearData = pairStepData('near');
          const nearLens = nearData.selectedLens || {};
          const nearFrames = frameItemsFor(nearData.selectedFrame || {}, nearData.extraFrames);
          const nearLensPrice = Number(nearLens.price) || 0;
          const nearDays = Number(nearLens.estimatedDays) || 0;
          // Every line of a two-pair order names its pair, frames included.
          const tagPair = (lines, visionPair) => lines.map((line) => ({
            ...line,
            specifications: { ...(line.specifications || {}), visionPair },
          }));
          pairs = [
            { visionPair: 'far', items: tagPair(items, 'far'), subtotal: framePrice + extraFramesTotal + lensPrice },
            {
              visionPair: 'near',
              items: tagPair([...nearFrames.lines, lensItemFor(nearLens, nearLens.name || 'Prescription Lenses', nearLensPrice, nearDays, 'near')], 'near'),
              subtotal: nearFrames.total + nearLensPrice,
            },
          ];
        }

        const finalSubtotal = pairs
          ? pairs.reduce((sum, pair) => sum + pair.subtotal, 0)
          : framePrice + extraFramesTotal + lensPrice;
        const finalTotal = finalSubtotal;
        const allItems = pairs
          ? pairs.flatMap((pair) => pair.items.map((item) => ({ ...item, visionPair: pair.visionPair })))
          : items;

        console.log('Payment calculation:', {
          framePrice,
          extraFramesTotal,
          lensPrice,
          lensName,
          orderType,
          itemsCount: items.length,
          total: finalTotal,
        });

        setOrderSummary({
          subtotal: finalSubtotal,
          tax: 0,
          discount: 0,
          total: finalTotal,
          items: allItems,
          pairs,
        });

        sessionStorage.setItem('orderSummary', JSON.stringify({
          subtotal: finalSubtotal,
          tax: 0,
          discount: 0,
          total: finalTotal,
          items: allItems,
          pairs,
        }));
      } catch (error) {
        console.error('Error calculating order summary:', error);
        setError(tr('payment.calcError'));
      }
    };

    calculateOrderSummary();
  }, []);

  const handleDepositChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setDeposit(value);
  };

  const calculateRemaining = () => {
    const amt = parseFloat(deposit) || 0;
    const remaining = orderSummary.total - amt;
    return remaining >= 0 ? remaining : 0;
  };

  const minDepositFiftyPercent =
    orderSummary.total > 0 ? Math.ceil(orderSummary.total * 0.5) : 0;

  const depositAmount = parseFloat(deposit) || 0;
  const paymentPercentage =
    orderSummary.total > 0
      ? Math.round((depositAmount / orderSummary.total) * 100)
      : 0;
  const hasMetMinimum = depositAmount >= minDepositFiftyPercent;
  const isEmpty = paymentPercentage === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const currentDepositAmount = parseFloat(deposit) || 0;
    if (currentDepositAmount < 0) {
      setError(tr('payment.depositNegative'));
      setLoading(false);
      return;
    }

    if (currentDepositAmount > orderSummary.total) {
      setError(tr('payment.depositExceed'));
      setLoading(false);
      return;
    }

    if (!salesPassesOrderUnderResponsibility && orderSummary.total > 0) {
      if (currentDepositAmount < minDepositFiftyPercent) {
        setError(
          `A minimum deposit of 50% (${minDepositFiftyPercent.toLocaleString()} DZD) is required. Increase the deposit or accept the responsibility waiver.`
        );
        setLoading(false);
        return;
      }
    }

    try {
      const paymentData = {
        method: 'cash',
        deposit: currentDepositAmount,
        remaining: calculateRemaining(),
        total: orderSummary.total,
        salesPassesOrderUnderResponsibility,
      };

      sessionStorage.setItem('paymentData', JSON.stringify(paymentData));
      navigate('/order-confirmation');
    } catch (error) {
      console.error('Error processing payment:', error);
      setError(tr('payment.processError'));
    } finally {
      setLoading(false);
    }
  };

  // Status pill content
  let statusTone = 'info';
  let statusIcon = <Info />;
  let statusText = (
    <>
      {tr('payment.enterADeposit')} <strong>50%+</strong> ({minDepositFiftyPercent.toLocaleString()} {tr('payment.dzdSkipsTheWaiver')}
    </>
  );

  if (!isEmpty && hasMetMinimum) {
    statusTone = 'ok';
    statusIcon = <CheckCircle2 />;
    statusText = (
      <>
        <strong>{tr('payment.minimumMetTitle')}</strong> {tr('payment.minimumMetDesc')}
      </>
    );
  } else if (!isEmpty && !hasMetMinimum) {
    statusTone = 'warn';
    statusIcon = <Shield />;
    statusText = (
      <>
        <strong>{tr('payment.below50Title')}</strong> {tr('payment.below50Desc', { amount: (minDepositFiftyPercent - depositAmount).toLocaleString() })}
      </>
    );
  }

  return (
    <Page>
      <Shell>
        <TopBar>
          <BackButton onClick={() => navigate('/measurement')}>
            <ArrowLeft size={14} />
            {tr('payment.back')}
          </BackButton>
        </TopBar>

        <TitleRow>
          <div>
            <Title>{tr('payment.collectDeposit')}</Title>
            <Subtitle>{tr('payment.subtitle')}</Subtitle>
          </div>
        </TitleRow>

        <form onSubmit={handleSubmit}>
          <Grid>
            {/* ── Left: payment input ──────────────────────────── */}
            <Card>
              <SectionHead>
                <SectionTitle>
                  <Wallet />
                  {tr('payment.section')}
                </SectionTitle>
              </SectionHead>

              {/* Compact progress block: percent + bar + paid/total */}
              <ProgressBlock>
                <ProgressPercent $met={hasMetMinimum && !isEmpty} $empty={isEmpty}>
                  {paymentPercentage}
                  <span className="suffix">%</span>
                </ProgressPercent>
                <ProgressBarStack>
                  <ProgressTrack>
                    <ProgressFill $percentage={paymentPercentage} $met={hasMetMinimum && !isEmpty} />
                    <ProgressMarker />
                  </ProgressTrack>
                  <ProgressLine>
                    <span>0</span>
                    <span><strong>50%</strong> {tr('payment.threshold')}</span>
                    <span>{orderSummary.total.toLocaleString()}</span>
                  </ProgressLine>
                </ProgressBarStack>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: t.inkMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tr('payment.paid')}</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: t.ink, fontVariantNumeric: 'tabular-nums' }}>
                    {depositAmount.toLocaleString()}
                  </div>
                </div>
              </ProgressBlock>

              <StatusPill tone={statusTone}>
                {statusIcon}
                <span>{statusText}</span>
              </StatusPill>

              {/* Amount field + chips on the same row */}
              <AmountLabel htmlFor="deposit">{tr('payment.depositAmount')}</AmountLabel>
              <InputRow>
                <AmountInputBox $focused={inputFocused}>
                  <AmountInput
                    type="text"
                    inputMode="decimal"
                    id="deposit"
                    name="deposit"
                    value={deposit}
                    onChange={handleDepositChange}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    placeholder="0"
                    disabled={loading}
                    autoComplete="off"
                  />
                  <AmountSuffix>{tr('payment.dzd')}</AmountSuffix>
                </AmountInputBox>

                <QuickChips>
                  <Chip
                    type="button"
                    $active={deposit === '0'}
                    onClick={() => setDeposit('0')}
                    disabled={loading}
                    title={tr('payment.noDeposit')}
                  >
                    <span className="label">{tr('payment.none')}</span>
                    <span className="amount">0</span>
                  </Chip>
                  <Chip
                    type="button"
                    $active={deposit === String(minDepositFiftyPercent)}
                    onClick={() => setDeposit(String(minDepositFiftyPercent))}
                    disabled={loading || orderSummary.total <= 0}
                    title={tr('payment.x50Deposit')}
                  >
                    <span className="label">50%</span>
                    <span className="amount">{minDepositFiftyPercent.toLocaleString()}</span>
                  </Chip>
                  <Chip
                    type="button"
                    $active={deposit === String(orderSummary.total)}
                    onClick={() => setDeposit(String(orderSummary.total))}
                    disabled={loading || orderSummary.total <= 0}
                    title={tr('payment.fullPayment')}
                  >
                    <span className="label">{tr('payment.full')}</span>
                    <span className="amount">{orderSummary.total.toLocaleString()}</span>
                  </Chip>
                </QuickChips>
              </InputRow>

              {/* Balance + (conditional) waiver, side by side */}
              <BalanceWaiverRow $showWaiver={!hasMetMinimum}>
                <BalancePill>
                  <div className="left">
                    <div className="icon">
                      <Receipt size={14} />
                    </div>
                    <div>
                      <div className="label">{tr('payment.balanceDue')}</div>
                      <div className="hint">{tr('payment.afterDeposit')}</div>
                    </div>
                  </div>
                  <div className="value">
                    {calculateRemaining().toLocaleString()} {tr('payment.dzd')}
                  </div>
                </BalancePill>

                {!hasMetMinimum && (
                  <WaiverCard $active={salesPassesOrderUnderResponsibility}>
                    <WaiverCheckbox
                      checked={salesPassesOrderUnderResponsibility}
                      onChange={(e) => setSalesPassesOrderUnderResponsibility(e.target.checked)}
                      disabled={loading}
                    />
                    <WaiverText>
                      <strong>{tr('payment.responsibilityWaiver')}</strong>
                      {tr('payment.below50IAcceptLiability')}
                    </WaiverText>
                  </WaiverCard>
                )}
              </BalanceWaiverRow>

              {error && (
                <ErrorBanner>
                  <AlertCircle />
                  <span>{error}</span>
                </ErrorBanner>
              )}

              <Footer>
                <SecondaryButton type="button" onClick={() => navigate('/measurement')}>
                  <ArrowLeft size={14} />
                  {tr('payment.back')}
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={loading}>
                  {loading ? tr('payment.processing') : tr('payment.continue')}
                  <ArrowRight size={15} />
                </PrimaryButton>
              </Footer>
            </Card>

            {/* ── Right: order summary (sticky) ───────────────── */}
            <SummaryCard>
              <SummaryEyebrow>
                <Sparkles size={12} />
                {tr('payment.orderSummary')}
              </SummaryEyebrow>

              <SummaryItems>
                {orderSummary.items && orderSummary.items.length > 0 ? (
                  orderSummary.items.map((item, idx) => {
                    const isFrame = item.type === 'frame';
                    const meta = [item.brand, item.model, item.material, item.color]
                      .filter(Boolean)
                      .join(' · ');
                    const pairHeading = item.visionPair && item.visionPair !== orderSummary.items[idx - 1]?.visionPair
                      ? tr(item.visionPair === 'near' ? 'pairs.pairNear' : 'pairs.pairFar')
                      : null;
                    return (
                      <React.Fragment key={idx}>
                      {pairHeading && <SummaryPairHeading>{pairHeading}</SummaryPairHeading>}
                      <SummaryItemRow>
                        <SummaryItemIcon>
                          {isFrame ? <Glasses size={14} /> : <Eye size={14} />}
                        </SummaryItemIcon>
                        <SummaryItemMain>
                          <SummaryItemName>{itemDisplayName(item)}</SummaryItemName>
                          {meta && <SummaryItemMeta>{meta}</SummaryItemMeta>}
                        </SummaryItemMain>
                        <SummaryItemPrice>
                          {item.totalPrice > 0
                            ? `${item.totalPrice.toLocaleString()}`
                            : '—'}
                        </SummaryItemPrice>
                      </SummaryItemRow>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <SummaryItemRow>
                    <SummaryItemMain>
                      <SummaryItemName style={{ color: 'rgba(255,255,255,0.6)' }}>
                        {tr('payment.noItemsSelected')}
                      </SummaryItemName>
                    </SummaryItemMain>
                  </SummaryItemRow>
                )}
              </SummaryItems>

              <SummaryDivider />

              <SummaryTotalRow>
                <SummaryTotalLabel>{tr('payment.total')}</SummaryTotalLabel>
                <SummaryTotalValue>
                  {orderSummary.total.toLocaleString()}
                  <SummaryTotalCurrency>{tr('payment.dzd')}</SummaryTotalCurrency>
                </SummaryTotalValue>
              </SummaryTotalRow>
            </SummaryCard>
          </Grid>
        </form>
      </Shell>
    </Page>
  );
};

export default Payment;
